import { useState, useEffect, useRef } from 'react';
import { useGlobalLoading } from './useGlobalLoading';

interface AppInitializationState {
  isInitializing: boolean;
  isComplete: boolean;
  progress: number;
  currentStep: string;
}

// Global flag para indicar quando a inicialização está completa
// Outros hooks podem verificar isso para evitar fetches desnecessários
let isAppInitialized = false;
export const getIsAppInitialized = () => isAppInitialized;
const setIsAppInitialized = (value: boolean) => {
  isAppInitialized = value;
};

export const useAppInitialization = () => {
  const { areAllCriticalFetchesComplete } = useGlobalLoading();
  const [state, setState] = useState<AppInitializationState>({
    isInitializing: true,
    isComplete: false,
    progress: 0,
    currentStep: 'Iniciando aplicação...'
  });

  const isRefreshRef = useRef(false);
  const startTimeRef = useRef(Date.now());
  const isCompleteRef = useRef(false);
  const initializationSteps = [
    'Verificando autenticação...',
    'Carregando configurações...',
    'Sincronizando dados do usuário...',
    'Preparando dashboard...',
    'Carregando leads...',
    'Configurando integrações...',
    'Otimizando performance...',
    'Finalizando inicialização...'
  ];

  // Atualizar ref quando isComplete mudar
  useEffect(() => {
    isCompleteRef.current = state.isComplete;
  }, [state.isComplete]);

  useEffect(() => {
    // Verificar se já foi inicializado recentemente (últimos 5 minutos)
    const lastInit = localStorage.getItem('app_last_initialization');
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;
    
    // Validar se o valor do lastInit é válido
    let isValidInit = false;
    if (lastInit) {
      const initTime = parseInt(lastInit);
      if (!isNaN(initTime) && initTime > 0 && (now - initTime) < FIVE_MINUTES && !isRefreshRef.current) {
        isValidInit = true;
      } else {
        // Valor inválido ou expirado - limpar
        localStorage.removeItem('app_last_initialization');
      }
    }
    
    // Se já foi inicializado recentemente e não é um refresh real, não reinicializar
    if (isValidInit) {
      setState(prev => ({
        ...prev,
        isInitializing: false,
        isComplete: true,
        progress: 100,
        currentStep: 'Aplicação carregada!'
      }));
      isCompleteRef.current = true;
      return;
    }

    // Detectar se é um refresh real da página
    const handleBeforeUnload = () => {
      isRefreshRef.current = true;
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // Se é um restore do cache do navegador (back/forward), não reinicializar
      // Usar ref para evitar problemas de closure
      if (event.persisted && isCompleteRef.current) {
        return;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pageshow', handlePageShow);
    };
    // Remover state.isComplete das dependências para evitar loop
     
  }, []); // Executar apenas uma vez na montagem

  // Ref to read the latest value of areAllCriticalFetchesComplete without re-running the effect.
  const criticalFetchesRef = useRef(areAllCriticalFetchesComplete);
  useEffect(() => {
    criticalFetchesRef.current = areAllCriticalFetchesComplete;
  }, [areAllCriticalFetchesComplete]);

  useEffect(() => {
    // Se já completou (via cache do localStorage no effect anterior), não criar interval
    if (isCompleteRef.current) return;

    // Verificar se já foi inicializado recentemente
    const lastInit = localStorage.getItem('app_last_initialization');
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;

    if (lastInit) {
      const initTime = parseInt(lastInit);
      if (isNaN(initTime) || initTime <= 0 || (now - initTime) >= FIVE_MINUTES) {
        localStorage.removeItem('app_last_initialization');
      }
    }

    let isMounted = true;
    let checkInterval: NodeJS.Timeout | null = null;

    const checkAndCompleteInitialization = () => {
      if (!isMounted) return;

      if (criticalFetchesRef.current) {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }

        const now = Date.now();
        const minSplashTime = 1500;
        const elapsedTime = Date.now() - startTimeRef.current;
        const remainingTime = Math.max(0, minSplashTime - elapsedTime);

        setTimeout(() => {
          if (isMounted) {
            setState(prev => ({
              ...prev,
              isInitializing: false,
              isComplete: true,
              progress: 100,
              currentStep: 'Aplicação carregada!'
            }));
            setIsAppInitialized(true);
            localStorage.setItem('app_last_initialization', now.toString());
          }
        }, remainingTime);
      }
    };

    const initializeApp = async () => {
      try {
        for (let i = 0; i < initializationSteps.length; i++) {
          if (!isMounted) break;

          const step = initializationSteps[i];
          const progress = ((i + 1) / initializationSteps.length) * 50;

          setState(prev => ({
            ...prev,
            currentStep: step,
            progress: Math.min(progress, 50)
          }));

          await new Promise(resolve => setTimeout(resolve, 150 + Math.random() * 100));
        }

        if (isMounted) {
          setState(prev => ({
            ...prev,
            currentStep: 'Aguardando carregamento de dados...',
            progress: 50
          }));

          // Se fetches já estão completos, completar imediatamente sem interval
          if (criticalFetchesRef.current) {
            checkAndCompleteInitialization();
          } else {
            // Só criar interval se realmente precisar aguardar
            checkInterval = setInterval(() => {
              checkAndCompleteInitialization();
            }, 100);
          }
        }
      } catch (error) {
        console.error('Erro durante inicialização:', error);
        if (isMounted) {
          setState(prev => ({
            ...prev,
            isInitializing: false,
            isComplete: true,
            progress: 100
          }));
        }
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once — reads areAllCriticalFetchesComplete via ref

  const resetInitialization = () => {
    localStorage.removeItem('app_last_initialization');
    setState({
      isInitializing: true,
      isComplete: false,
      progress: 0,
      currentStep: 'Iniciando aplicação...'
    });
  };

  return {
    ...state,
    resetInitialization
  };
};

